import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Separator } from '@/components/ui'
import { useSEO, createArticleSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider'

export default function BrassCityJazzCaseStudyPage() {
  const breadcrumbs = generateBreadcrumbs('/blog/brass-city-jazz-case-study', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const sectionsRef = useScrollReveal<HTMLElement>('.article-section', { stagger: 0.1 })

  useSEO({
    title: 'Putting a Free Jazz Festival’s Lineup Back on Stage',
    description:
      'A redesign concept for Brass City Jazz Fest: a fast, cinematic site where the Billboard-charting lineup leads, the sponsors who fund it are finally visible, and everything works on a phone. Built from their own public content.',
    url: '/blog/brass-city-jazz-case-study',
    canonical: '/blog/brass-city-jazz-case-study',
    type: 'article',
    breadcrumbs,
    article: {
      publishedTime: '2026-06-24',
      author: 'Garrett John Law',
      section: 'Case Studies',
      tags: ['website redesign', 'music festival', 'wordpress', 'the full score', 'jazz'],
    },
    schema: createArticleSchema({
      headline: 'Putting a Free Jazz Festival’s Lineup Back on Stage',
      description:
        'A redesign concept for Brass City Jazz Fest: a fast, cinematic festival site where the lineup leads and the sponsors are finally visible.',
      datePublished: '2026-06-24',
      url: '/blog/brass-city-jazz-case-study',
    }),
  })

  return (
    <article ref={sectionsRef} className="mx-auto max-w-3xl space-y-12 px-4 py-16">
      {/* Header */}
      <header ref={heroRef} className="space-y-4">
        <Link to="/blog" className="text-primary text-sm hover:underline">
          &larr; Back to Blog
        </Link>
        <h1 className="text-fluid-h2">Putting a Free Jazz Festival’s Lineup Back on Stage</h1>
        <div className="text-muted-foreground flex items-center gap-4 text-sm">
          <time dateTime="2026-06-24">June 24, 2026</time>
          <span>&middot;</span>
          <span>4 min read</span>
        </div>
        <p className="text-fluid-h4 text-muted-foreground">
          Brass City Jazz Fest is a free, Billboard-charting night of jazz in Downtown Waterbury. Its website was hiding
          the two things that keep it free: the lineup people come for, and the sponsors who pay for it. So we rebuilt
          it.
        </p>
      </header>

      {/* Cover: drag-to-compare hero */}
      <figure className="not-prose">
        <BeforeAfterSlider
          beforeSrc="/images/blog/brass-city-jazz/before-home.webp"
          afterSrc="/images/blog/brass-city-jazz/after-home.webp"
          beforeAlt="The original Brass City Jazz Fest homepage on WordPress: a clip-art logo, a cluttered menu, and a newsletter box, with no artists in sight"
          afterAlt="The rebuilt Brass City Jazz Fest homepage: a saxophonist lit gold against a dark club, the festival name in marquee type, and a Lineup button"
          beforeLabel="WordPress"
          afterLabel="Rebuilt"
        />
        <figcaption className="text-muted-foreground mt-3 text-sm">
          Drag the handle to compare: the live WordPress site and the rebuilt demo, same screen. We built the redesign
          from the festival’s own public content: it isn’t a client engagement, it’s a working demo of what the site
          could be.
        </figcaption>
      </figure>

      <Separator />

      {/* The Goal */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">A Free Festival Has Two Jobs for Its Website</h2>
        <p>
          When admission is free, the website is not a ticket page. It has two jobs, and they pay for each other. It has
          to make someone want to show up, and for a festival booking #1 Billboard artists, the way you do that is by
          putting the <strong>lineup</strong> out front. And it has to make the <strong>sponsors</strong> who fund the
          whole thing look good, because next year’s festival depends on them coming back.
        </p>
        <p>The old site did neither. The lineup was buried and the sponsors’ page was broken.</p>
      </section>

      {/* Visual: the rebuilt home, early */}
      <figure className="not-prose article-section space-y-2">
        <div className="border-border h-[28rem] overflow-y-auto rounded-lg border">
          <img
            src="/images/blog/brass-city-jazz/home-full.webp"
            alt="The full rebuilt Brass City Jazz Fest homepage, top to bottom: the gold-sax hero, the marquee lineup board, festival details, and sponsors"
            width={1200}
            height={3043}
            loading="lazy"
            className="w-full"
          />
        </div>
        <figcaption className="text-muted-foreground text-sm">
          The rebuilt homepage, top to bottom. The lineup is the first thing you meet, framed like a club marquee.
        </figcaption>
      </figure>

      <Separator />

      {/* The Problem */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">What the Old Site Was Costing Them</h2>
        <p>
          The site ran on a generic WordPress theme, and it was quietly working against the festival. We graded it
          against{' '}
          <Link to="/how-we-measure" className="text-primary hover:underline">
            The Full Score
          </Link>
          , our six-pillar standard. It came back a <strong>62, a D</strong>. The gaps were not cosmetic:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            The <strong>Sponsors &amp; Supporters page was broken</strong>, it threw a server error and would not load
            at all. The companies paying to keep the festival free were paying for a page no one could open.
          </li>
          <li>
            There was <strong>no About page and no Contact page</strong>: both 404, not even in the sitemap. A
            seven-year-old festival with nowhere to tell its story or take a question.
          </li>
          <li>
            The homepage led with a <strong>SoundCloud embed</strong>, not the artists. The 2026 lineup was hard to find
            behind last year’s content.
          </li>
          <li>
            Search engines barely understood the site: it scored <strong>60 on SEO</strong>, so a family Googling “jazz
            festival Waterbury” might never land on it.
          </li>
        </ul>
      </section>

      <Separator />

      {/* The Build */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Rebuild</h2>
        <p>
          We kept every real fact (the dates, the billings, the sponsors, the hotel block) and rebuilt the experience
          around the lineup. The festival’s brass-and-midnight palette became the whole mood: a cinematic photo of
          headliner Ellis Hamilton, the name set in marquee type, and the artists billed like a venue’s “tonight on
          stage” board.
        </p>
        <p className="!mb-2">
          And the pages that used to be broken or missing now do their jobs. Scroll each to see the whole page:
        </p>
        <div className="not-prose grid gap-4 sm:grid-cols-3">
          <figure className="space-y-2">
            <div className="border-border h-96 overflow-y-auto rounded-lg border">
              <img
                src="/images/blog/brass-city-jazz/lineup-full.webp"
                alt="The full Lineup page: each 2026 artist as a billed marquee card with their instrument and Billboard billing"
                width={1200}
                height={3200}
                loading="lazy"
                className="w-full"
              />
            </div>
            <figcaption className="text-muted-foreground text-xs">The lineup, billed like a marquee.</figcaption>
          </figure>
          <figure className="space-y-2">
            <div className="border-border h-96 overflow-y-auto rounded-lg border">
              <img
                src="/images/blog/brass-city-jazz/sponsors-full.webp"
                alt="The full Sponsors page, now loading: presenting partners, supporters, and clear sponsorship levels"
                width={1200}
                height={1862}
                loading="lazy"
                className="w-full"
              />
            </div>
            <figcaption className="text-muted-foreground text-xs">Sponsors, finally visible, and loading.</figcaption>
          </figure>
          <figure className="space-y-2">
            <div className="border-border h-96 overflow-y-auto rounded-lg border">
              <img
                src="/images/blog/brass-city-jazz/festival-info-full.webp"
                alt="The full Festival Info page: date, time, location, admission, and the Marriott hotel discount"
                width={1200}
                height={2097}
                loading="lazy"
                className="w-full"
              />
            </div>
            <figcaption className="text-muted-foreground text-xs">
              Everything a first-timer needs, in one place.
            </figcaption>
          </figure>
        </div>
        <p>
          It is a custom static build: fast, owned outright, nothing to patch every month. The kind of site you can hand
          to a sponsor without wincing.
        </p>
      </section>

      <Separator />

      {/* The Result */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Difference, Measured</h2>
        <p>
          We ran the rebuild back through the same Full Score checker. The site went from a 62 to a 94, a D to an A,
          with the things that decide whether a free event fills a park, search and speed and the phone experience, at
          or near perfect, and the broken pages back to life.
        </p>

        {/* Metrics */}
        <div className="my-8 grid gap-6 text-center md:grid-cols-3">
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">62 → 94</p>
            <p className="text-muted-foreground text-sm">Full Score, a D to an A</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">60 → 100</p>
            <p className="text-muted-foreground text-sm">Search visibility, now easy to find</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">Broken → live</p>
            <p className="text-muted-foreground text-sm">The sponsors page loads again</p>
          </div>
        </div>

        <p>
          We hold our own work to the same standard. The checker flagged real contrast problems on our first pass, the
          footer copy and the lineup text were too faint, so we fixed them. That is the whole point of grading against a
          standard: it catches the gaps on our sites as readily as on anyone else’s.
        </p>
      </section>

      <Separator />

      {/* Lessons */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">What This Means for Your Festival</h2>
        <p>
          Most festivals are not losing attendance because their website is ugly. They are losing it because the site is
          slow, hard to find in search, and broken in the exact places that matter, the lineup that draws the crowd, the
          sponsor page that funds the day. The thing you exist to put on stage ends up buried three clicks deep.
        </p>
        <p>
          The fix is not a new coat of paint. It is pointing the site at the one or two things you actually need it to
          do, making it fast and findable, and getting the broken pieces working again. We grade honestly against a
          standard, fix the gaps that matter, and put the music back out front.
        </p>
      </section>

      <Separator />

      {/* CTA */}
      <section className="bg-primary/5 border-primary/20 article-section space-y-6 rounded-lg border p-8 text-center">
        <h3 className="text-2xl font-semibold">Want to See Your Own Full Score?</h3>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          We grade your current site against the same six pillars and hand you the list of gaps, whether or not you ever
          hire us. If the gaps are worth fixing, we can rebuild it the way we did for Brass City Jazz Fest.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/contact">
              <Button variant="default" size="lg" className="cursor-pointer">
                Get Your Full Score &rarr;
              </Button>
            </Link>
          </m.div>
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/how-we-measure">
              <Button variant="outline" size="lg" className="cursor-pointer">
                How We Measure
              </Button>
            </Link>
          </m.div>
        </div>
      </section>

      <Separator />

      {/* Author Bio */}
      <section className="border-border border-t pt-8">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-lg font-semibold">About the Author</p>
            <p className="text-muted-foreground mt-2">
              <strong>Garrett John Law</strong> is a software engineer and musician who builds digital platforms for
              festivals, schools, and arts organizations. He’s an Interlochen alum and CIM graduate, and the founder of
              Fugue Lab, where he rebuilds the websites music organizations rely on to fill the room.
            </p>
            <div className="mt-4">
              <Link to="/about" className="text-primary text-sm hover:underline">
                Learn More about Fugue Lab &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  )
}
